import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCamera,
  faEye,
  faEyeSlash,
  faSave,
  faTimes,
  faLock,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../configuration/api";
import styles from "./UserProfile.module.css";
import { uploadAttachment } from "../../service/file-service";
import { UserInterface } from "../../interface/userInterface";

interface UserProfile {
  _id: string;
  name: string;
  emailAddress: string;
  contactNumber?: string;
  city?: string;
  state?: string;
  picture?: {
    path: string;
    filename: string;
  };
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfile: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile");
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [message, setMessage] = useState<{ type: string; text: string } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states
  const [profileData, setProfileData] = useState({
    name: "",
    contactNumber: "",
    city: "",
    state: "",
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/v1/user/profile");
      const userData = response.data.data;
      setUser(userData);
      setProfileData({
        name: userData.name,
        contactNumber: userData.contactNumber || "",
        city: userData.city || "",
        state: userData.state || "",
      });
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      setMessage({ type: "error", text: "Failed to load profile data" });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      setMessage({ type: "error", text: "Image must be less than 5MB" });
      return;
    }

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file" });
      return;
    }

    try {
      setUploading(true);
      const imageResponse = (await uploadAttachment(file)).file;

      const updatedUser: UserInterface = await axiosInstance.patch(
        "/api/v1/user/profile/picture",
        {
          imageResponse,
        }
      );

      setUser((prev: any) =>
        prev ? { ...prev, picture: imageResponse } : null
      );
      setMessage({
        type: "success",
        text: "Profile picture updated successfully!",
      });
    } catch (error) {
      console.error("Failed to upload image:", error);
      setMessage({ type: "error", text: "Failed to upload image" });
    } finally {
      setUploading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await axiosInstance.patch(
        "/api/v1/user/profile",
        profileData
      );
      setUser((prev) => (prev ? { ...prev, ...response.data.data } : null));
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (error) {
      console.error("Failed to update profile:", error);
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters long",
      });
      return;
    }

    try {
      setSaving(true);
      await axiosInstance.patch("/api/v1/user/password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setMessage({ type: "success", text: "Password changed successfully!" });
    } catch (error: any) {
      console.error("Failed to change password:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to change password",
      });
    } finally {
      setSaving(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Profile Management</h1>
        <p>Manage your personal information and security settings</p>
      </div>

      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          <FontAwesomeIcon
            icon={
              message.type === "success" ? faCheckCircle : faExclamationCircle
            }
          />
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className={styles.closeMessage}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className={styles.profileContent}>
        {/* Profile Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.profileCard}>
            <div className={styles.avatarSection}>
              <div className={styles.avatar} onClick={triggerFileInput}>
                {user?.picture?.path ? (
                  <img
                    src={user.picture.path}
                    alt="Profile"
                    className={styles.avatarImage}
                  />
                ) : (
                  <div className={styles.avatarPlaceholder}>
                    <FontAwesomeIcon icon={faUser} size="3x" />
                  </div>
                )}
                <div className={styles.avatarOverlay}>
                  <FontAwesomeIcon icon={faCamera} />
                  <span>Change Photo</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className={styles.fileInput}
                />
              </div>
              {uploading && (
                <div className={styles.uploadingOverlay}>
                  <div className={styles.uploadSpinner}></div>
                </div>
              )}
              <h2 className={styles.userName}>{user?.name}</h2>
              <p className={styles.userEmail}>{user?.emailAddress}</p>
            </div>

            <nav className={styles.sidebarNav}>
              <button
                className={`${styles.navItem} ${
                  activeTab === "profile" ? styles.active : ""
                }`}
                onClick={() => setActiveTab("profile")}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>Profile Information</span>
              </button>
              <button
                className={`${styles.navItem} ${
                  activeTab === "password" ? styles.active : ""
                }`}
                onClick={() => setActiveTab("password")}
              >
                <FontAwesomeIcon icon={faLock} />
                <span>Change Password</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {activeTab === "profile" ? (
            <form onSubmit={handleProfileSubmit} className={styles.form}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  <FontAwesomeIcon icon={faUser} />
                  Personal Information
                </h3>

                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="email">
                      <FontAwesomeIcon icon={faEnvelope} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user?.emailAddress || ""}
                      disabled
                      className={styles.disabledInput}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="contactNumber">
                      <FontAwesomeIcon icon={faPhone} />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="contactNumber"
                      name="contactNumber"
                      value={profileData.contactNumber}
                      onChange={handleProfileChange}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  Location Information
                </h3>

                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={profileData.city}
                      onChange={handleProfileChange}
                      placeholder="Enter your city"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={profileData.state}
                      onChange={handleProfileChange}
                      placeholder="Enter your state"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={saving}
                  className={styles.saveButton}
                >
                  {saving ? (
                    <>
                      <div className={styles.buttonSpinner}></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className={styles.form}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  <FontAwesomeIcon icon={faLock} />
                  Change Password
                </h3>
                <p className={styles.sectionDescription}>
                  For security reasons, please enter your current password to
                  set a new one.
                </p>

                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="currentPassword">Current Password *</label>
                    <div className={styles.passwordInputGroup}>
                      <input
                        type={showPassword.current ? "text" : "password"}
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility("current")}
                        className={styles.passwordToggle}
                      >
                        <FontAwesomeIcon
                          icon={showPassword.current ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="newPassword">New Password *</label>
                    <div className={styles.passwordInputGroup}>
                      <input
                        type={showPassword.new ? "text" : "password"}
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility("new")}
                        className={styles.passwordToggle}
                      >
                        <FontAwesomeIcon
                          icon={showPassword.new ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                    <div className={styles.passwordHint}>
                      Must be at least 6 characters long
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">
                      Confirm New Password *
                    </label>
                    <div className={styles.passwordInputGroup}>
                      <input
                        type={showPassword.confirm ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility("confirm")}
                        className={styles.passwordToggle}
                      >
                        <FontAwesomeIcon
                          icon={showPassword.confirm ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={saving}
                  className={styles.saveButton}
                >
                  {saving ? (
                    <>
                      <div className={styles.buttonSpinner}></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faLock} />
                      Update Password
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
